import * as React from 'react'
import { cn } from '@/shared/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip'

export type PersonStackItem = {
  /** Llave estable de la fila. Es también el `seed` del color del avatar. */
  id: string
  name: string
  photo?: string | null
  /** Línea secundaria del tooltip (p. ej. «Cliente 245»). */
  caption?: string | null
}

type CellPeopleStackProps = React.HTMLAttributes<HTMLDivElement> & {
  people: PersonStackItem[]
  /** Cuántos avatares se apilan antes de resumir en «+N». */
  maxVisible?: number
  emptyLabel?: string
  /** Cómo se nombra el conjunto en la línea de abajo: `[singular, plural]`. */
  countLabel?: [string, string]
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
}

/**
 * Un GRUPO de personas en una celda: avatares apilados + el primero con nombre.
 *
 * Headless del dominio —no sabe de propietarios, asesores ni firmantes—, así que
 * sirve para cualquier columna que muestre varias personas. Espeja el look de
 * `cell-negotiation-owners`, que es el original: aquél sigue en su feature porque
 * depende del tipo v1 de negociaciones y se muda acá cuando ese módulo migre.
 *
 * NO fabrica identidades: una persona sin nombre se descarta en vez de dibujarse
 * como un avatar anónimo. Si no queda ninguna, la celda lo dice.
 */
export const CellPeopleStack = React.forwardRef<HTMLDivElement, CellPeopleStackProps>(
  (
    {
      people,
      maxVisible = 4,
      emptyLabel = '—',
      countLabel = ['persona', 'personas'],
      className,
      ...props
    },
    ref
  ) => {
    const resolved = people.filter((person) => Boolean(person?.name))
    if (resolved.length === 0) {
      return <span className={cn('text-muted-foreground', className)}>{emptyLabel}</span>
    }

    const primary = resolved[0]
    const hiddenCount = Math.max(0, resolved.length - maxVisible)
    const [singular, plural] = countLabel

    return (
      <div
        ref={ref}
        className={cn('flex min-w-[220px] items-center gap-3 py-1', className)}
        {...props}
      >
        <TooltipProvider delayDuration={0}>
          <div className="flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background">
            {resolved.slice(0, maxVisible).map((person, index) => (
              <Tooltip key={person.id}>
                <TooltipTrigger asChild>
                  <Avatar
                    seed={person.id}
                    className="h-8 w-8 select-none"
                    style={{ zIndex: resolved.length - index }}
                  >
                    <AvatarImage src={person.photo ?? undefined} className="object-cover" />
                    <AvatarFallback className="text-[10px] uppercase">
                      {initials(person.name) || '?'}
                    </AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent className="space-y-0.5">
                  <p className="max-w-[24ch] text-xs font-semibold">{person.name}</p>
                  {person.caption && (
                    <p className="text-xs text-muted-foreground">{person.caption}</p>
                  )}
                </TooltipContent>
              </Tooltip>
            ))}
            {hiddenCount > 0 && (
              <Avatar className="h-8 w-8" style={{ zIndex: 0 }}>
                <AvatarFallback className="bg-muted text-[10px] font-semibold text-foreground">
                  +{hiddenCount}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </TooltipProvider>

        <div className="min-w-0">
          <p className="max-w-[190px] truncate text-sm font-medium">{primary.name}</p>
          <p className="text-xs text-muted-foreground">
            {resolved.length === 1 ? `1 ${singular}` : `${resolved.length} ${plural}`}
          </p>
        </div>
      </div>
    )
  }
)
CellPeopleStack.displayName = 'CellPeopleStack'
